import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Skeleton,
  Fab,
  useMediaQuery,
  useTheme as useMuiTheme,
  Checkbox,
  TablePagination,
  Menu,
  ListItemIcon,
  ListItemText,
  Collapse,
} from '@mui/material';
import {
  PlusIcon,
  PencilSquareIcon,
  TrashIcon,
  ArrowPathIcon,
  ArrowDownTrayIcon,
  BanknotesIcon,
  XMarkIcon,
  EllipsisVerticalIcon,
  FunnelIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import useActionParam from '../../hooks/useActionParam';
import { useTheme } from '../../context/ThemeContext';
import { useConfirm } from '../../context/ConfirmContext';
import { useAuth } from '../../context/AuthContext';
import { financeApi } from '../../api/financeApi';
import { formatCurrency, CURRENCY } from '../../config/currency';
import Modal from '../../components/Modal';
import { downloadBlob } from '../../utils/downloadBlob';
import FinanceTabBar from '../../components/finance/FinanceTabBar';
import PaymentModeSelect from '../../components/finance/PaymentModeSelect';
import FinanceDateRange from '../../components/finance/FinanceDateRange';
import FilteredTotals from '../../components/finance/FilteredTotals';
import {
  PrimaryButton,
  SecondaryButton,
  FormActions,
  AppIconButton,
  PageHeroBand,
  SearchFilterBar,
  DataTable,
  StatusChip,
  FormInput,
  EmptyState,
} from '../../components/ui';
import type { DataTableColumn } from '../../components/ui/DataTable';
import type { FilterConfig } from '../../components/ui/SearchFilterBar';
import type { DateRangePreset } from '../../components/ui/DateRangePicker';
import { thisMonthRange, lastMonthRange, todayLocalIso } from '../../utils/dateLocal';
import { useFilterParams } from '../../hooks/useFilterParams';
import { useFuzzy } from '../../utils/fuzzySearch';

// Compact "enterprise" table density (Item 9): tighter cells, uppercase
// tertiary header row, token-driven row hover.
const denseTableSx = {
  '& .MuiTableCell-root': { padding: '8px 12px' },
  '& .MuiTableCell-head': {
    fontSize: 12,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    color: 'var(--text-tertiary)',
  },
  '& .MuiTableBody-root .MuiTableRow-root:hover': {
    backgroundColor: 'var(--bg-hover)',
  },
} as const;

// --- Types ---

interface IncomeEntry {
  id: number;
  source: string;
  category: string;
  amount: number;
  vatAmount: number | null;
  date: string;
  fromDate?: string | null;
  toDate?: string | null;
  recordedDate?: string | null;
  paymentMode: string;
  type: 'MANUAL' | 'POS' | 'ADJUSTMENT' | 'SETTLEMENT';
  description?: string | null;
  createdAt: string;
}

interface IncomeListResponse {
  content: IncomeEntry[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
}

interface IncomeSummary {
  totalIncome: number;
  manualIncome: number;
  posIncome: number;
  byMode?: Record<string, number>;
}

interface IncomeFormData {
  id?: number;
  amount: number | '';
  vatAmount: number | '';
  source: string;
  category: string;
  fromDate: string;        // required (was: date)
  toDate?: string;         // optional — leave blank for single day
  recordedDate?: string;   // optional — defaults to today
  date?: string;           // legacy: kept so we can hydrate from existing rows
  paymentMode: string;
  description: string;
}

interface Filters {
  search: string;
  category: string;
  paymentMode: string;
  type: string;
  dateFrom: string;
  dateTo: string;
}

type PeriodView = 'DAILY' | 'WEEKLY' | 'MONTHLY';

// --- Constants ---

interface IncomeCategoryItem {
  id: number;
  name: string;
  displayName: string;
  isSystem: boolean;
}

const FALLBACK_INCOME_CATEGORIES = [
  { value: 'DAILY_SALES', label: 'Daily Sales' },
  { value: 'CATERING', label: 'Catering' },
  { value: 'EVENTS', label: 'Events' },
  { value: 'TIPS', label: 'Tips' },
  { value: 'RENTAL', label: 'Rental' },
  { value: 'DELIVERY_COMMISSION', label: 'Delivery Commission' },
  { value: 'OTHER', label: 'Other' },
];

const PAYMENT_MODES = [
  { value: 'CASH', label: 'Cash' },
  { value: 'BANK_TRANSFER', label: 'Bank Transfer' },
  { value: 'UPI', label: 'UPI' },
  { value: 'CARD', label: 'Card' },
];

const TYPE_VARIANT_MAP: Record<string, { label: string; variant: 'info' | 'success' | 'warning' }> = {
  MANUAL: { label: 'Manual', variant: 'info' },
  POS: { label: 'POS', variant: 'success' },
  ADJUSTMENT: { label: 'Adjustment', variant: 'warning' },
  // SETTLEMENT rows are auto-derived from aggregator settlements (treat
  // visually like POS — green/success — since they are also auto-derived
  // and reflect actual bank-paid revenue rather than manual input).
  SETTLEMENT: { label: 'Settlement', variant: 'success' },
};

const PAYMENT_MODE_LABELS: Record<string, string> = {
  CASH: 'Cash',
  BANK_TRANSFER: 'Bank',
  UPI: 'UPI',
  CARD: 'Card',
};

const getThisMonthRange = () => thisMonthRange();

const DEFAULT_FILTERS: Filters = {
  search: '',
  category: '',
  paymentMode: '',
  type: '',
  ...getThisMonthRange(),
};

const ROWS_PER_PAGE_OPTIONS = [10, 25, 50];

const getDefaultFormData = (): IncomeFormData => {
  const today = todayLocalIso();
  return {
    amount: '',
    vatAmount: '',
    source: '',
    category: '',
    fromDate: today,
    toDate: '',
    recordedDate: today,
    paymentMode: 'CASH',
    description: '',
  };
};

const DATE_PRESETS: DateRangePreset[] = [
  {
    label: 'This Month',
    getValue: () => {
      const r = thisMonthRange();
      return { start: r.dateFrom, end: r.dateTo };
    },
  },
  {
    label: 'Last Month',
    getValue: () => {
      const r = lastMonthRange();
      return { start: r.dateFrom, end: r.dateTo };
    },
  },
  {
    label: 'All Time',
    getValue: () => ({ start: '', end: '' }),
  },
];

// --- Component ---

const IncomeManagement: React.FC = () => {
  const { theme } = useTheme();
  const confirm = useConfirm();
  const { user } = useAuth();
  const navigate = useNavigate();
  // Finance-only-restricted stewards can ADD income entries but must not see
  // Edit/Delete affordances. The backend also enforces this with a 403, but
  // hiding the UI keeps the experience clean (no broken-looking actions).
  const isFinanceOnlyRestricted = Boolean(user?.financeOnlySteward);
  // Deep-link: dashboard Quick Action opens Record Income.
  const handleOpenAddRef = React.useRef<() => void>();
  useActionParam('add', true, () => handleOpenAddRef.current?.());
  const isMobile = useMediaQuery(useMuiTheme().breakpoints.down('sm'));

  // Data state
  const [incomes, setIncomes] = useState<IncomeEntry[]>([]);
  const [totalElements, setTotalElements] = useState(0);
  const [summary, setSummary] = useState<IncomeSummary>({
    totalIncome: 0,
    manualIncome: 0,
    posIncome: 0,
  });

  // Income categories from API
  const [incomeCategories, setIncomeCategories] = useState<IncomeCategoryItem[]>([]);

  // Aggregator channel names (lowercased). Used to block manual income entries
  // whose source matches an ONLINE_AGGREGATOR channel — those rows must come
  // via Aggregator Settlements so income matches the actual bank payout.
  const [aggregatorChannelNames, setAggregatorChannelNames] = useState<string[]>([]);

  const allIncomeCategories = useMemo(() => {
    // Hide the system "Aggregator Settlement" category from the manual Add
    // Income picker — those rows are auto-created when a settlement is saved.
    const isAggregatorSettlementCategory = (name: string) =>
      name.toLowerCase().replace(/[_\s]/g, '') === 'aggregatorsettlement';

    if (incomeCategories.length > 0) {
      return incomeCategories
        .filter((cat) => !(cat.isSystem && isAggregatorSettlementCategory(cat.name)))
        .map((cat) => ({
          value: cat.name,
          label: cat.displayName || cat.name,
          id: cat.id,
        }));
    }
    return FALLBACK_INCOME_CATEGORIES.map((cat) => ({ ...cat, id: 0 }));
  }, [incomeCategories]);

  // UI state
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filters, setFiltersPatch] = useFilterParams<Filters>(DEFAULT_FILTERS, { prefix: 'inc_' });
  const setFilters = useCallback(
    (next: Filters | ((prev: Filters) => Filters)) => {
      if (typeof next === 'function') {
        setFiltersPatch(next(filters));
      } else {
        setFiltersPatch(next);
      }
    },
    [setFiltersPatch, filters],
  );
  const [periodView, setPeriodView] = useState<PeriodView>('DAILY');
  const [showFilters, setShowFilters] = useState(() => {
    try { return localStorage.getItem('finance_income_filters_open') === '1'; } catch { return false; }
  });

  const toggleFilters = () => {
    setShowFilters((prev) => {
      const next = !prev;
      try { localStorage.setItem('finance_income_filters_open', next ? '1' : '0'); } catch { /* ignore */ }
      return next;
    });
  };

  const activeFilterCount = useMemo(() => {
    let n = 0;
    if (filters.search) n++;
    if (filters.category) n++;
    if (filters.paymentMode) n++;
    if (filters.type) n++;
    return n;
  }, [filters]);

  // Form modal state
  const [formOpen, setFormOpen] = useState(false);
  const [formData, setFormData] = useState<IncomeFormData>(getDefaultFormData());
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  // Match the current source against the aggregator name list (case-insensitive,
  // trimmed). Returns the original channel name when matched so we can show it
  // in the inline banner.
  const aggregatorSourceMatch = useMemo(() => {
    const trimmed = formData.source?.trim().toLowerCase();
    if (!trimmed) return null;
    return aggregatorChannelNames.find((n) => n === trimmed) || null;
  }, [formData.source, aggregatorChannelNames]);

  // Bulk update checkbox
  const [applyToSimilar, setApplyToSimilar] = useState(false);

  // Export state
  const [exporting, setExporting] = useState(false);

  const isEdit = Boolean(formData.id);

  // Actions menu state (per-row ⋯ menu)
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [menuActiveId, setMenuActiveId] = useState<number | null>(null);

  const handleMenuOpen = (e: React.MouseEvent<HTMLElement>, id: number) => {
    e.stopPropagation();
    setMenuAnchorEl(e.currentTarget);
    setMenuActiveId(id);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setMenuActiveId(null);
  };

  // --- Computed ---

  const hasActiveFilters = useMemo(
    () =>
      filters.search !== '' ||
      filters.category !== '' ||
      filters.paymentMode !== '' ||
      filters.type !== '' ||
      filters.dateFrom !== '' ||
      filters.dateTo !== '',
    [filters]
  );

  // --- Data Fetching ---

  const fetchIncome = useCallback(async () => {
    try {
      const searchActive = !!filters.search;
      const params: Record<string, unknown> = {
        page: searchActive ? 0 : page,
        size: searchActive ? 500 : rowsPerPage,
        period: periodView,
      };
      // When search active we fetch a broad page and apply fuzzy client-side
      // (so typos like "jest" still match "Just Eat"). Skip backend `search`.
      if (filters.category) params.category = filters.category;
      if (filters.paymentMode) params.paymentMode = filters.paymentMode;
      if (filters.type) params.type = filters.type;
      if (filters.dateFrom) params.dateFrom = filters.dateFrom;
      if (filters.dateTo) params.dateTo = filters.dateTo;

      const res = await financeApi.getIncome(params);

      if (Array.isArray(res.data)) {
        setIncomes(res.data);
        setTotalElements(res.data.length);
      } else {
        const data: IncomeListResponse = res.data;
        setIncomes(data.content || []);
        setTotalElements(data.totalElements || 0);
        // Extract summary from list response (more reliable than separate summary endpoint)
        if (data.summary) {
          setSummary({
            totalIncome: data.summary.grandTotal || data.summary.totalIncome || 0,
            manualIncome: data.summary.totalManual || data.summary.manualIncome || 0,
            posIncome: data.summary.totalPOS || data.summary.posIncome || 0,
          });
        }
      }
    } catch (err: unknown) {
      console.error('Error fetching income:', err);
      toast.error('Failed to load income records');
    }
  }, [page, rowsPerPage, filters, periodView]);

  const fetchSummary = useCallback(async () => {
    try {
      const params: Record<string, unknown> = { period: periodView };
      if (filters.dateFrom) params.dateFrom = filters.dateFrom;
      if (filters.dateTo) params.dateTo = filters.dateTo;
      const res = await financeApi.getIncomeSummary(params);
      if (res.data) {
        const d = res.data as unknown as Record<string, number>;
        setSummary({
          totalIncome: d.grandTotal ?? d.totalIncome ?? 0,
          manualIncome: d.totalManual ?? d.manualIncome ?? 0,
          posIncome: d.totalPOS ?? d.posIncome ?? 0,
        });
      }
    } catch (err: unknown) {
      console.error('Error fetching income summary:', err);
    }
  }, [periodView, filters.dateFrom, filters.dateTo]);

  // Fetch income categories from API
  const fetchIncomeCategories = useCallback(async () => {
    try {
      const res = await financeApi.getIncomeCategories();
      const data = Array.isArray(res.data) ? res.data : [];
      setIncomeCategories(data);
    } catch (err) {
      console.error('Error fetching income categories:', err);
    }
  }, []);

  // Fetch revenue channels — used to block manual income for aggregator channels.
  const fetchAggregatorChannels = useCallback(async () => {
    try {
      const res = await financeApi.getChannels();
      const channels = Array.isArray(res.data) ? res.data : [];
      const names = channels
        .filter((c) => (c.channelType || c.type) === 'ONLINE_AGGREGATOR')
        .map((c) => c.name.trim().toLowerCase());
      setAggregatorChannelNames(names);
    } catch (err) {
      console.error('Error fetching revenue channels:', err);
    }
  }, []);

  // Initial load
  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await Promise.all([fetchIncome(), fetchSummary(), fetchIncomeCategories(), fetchAggregatorChannels()]);
      setLoading(false);
    };
    init();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Refetch on param changes
  useEffect(() => {
    if (!loading) {
      fetchIncome();
    }
  }, [page, rowsPerPage, filters, periodView]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!loading) {
      fetchSummary();
    }
  }, [periodView, filters.dateFrom, filters.dateTo]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRefresh = async () => {
    setRefreshing(true);
    await Promise.all([fetchIncome(), fetchSummary()]);
    setRefreshing(false);
  };

  const handleExportCsv = async () => {
    setExporting(true);
    const toastId = toast.loading('Exporting...');
    try {
      const params: Record<string, unknown> = { format: 'CSV' };
      if (filters.dateFrom) params.dateFrom = filters.dateFrom;
      if (filters.dateTo) params.dateTo = filters.dateTo;
      if (filters.category) params.category = filters.category;
      if (filters.paymentMode) params.paymentMode = filters.paymentMode;
      if (filters.type) params.type = filters.type;
      if (filters.search) params.search = filters.search;
      const res = await financeApi.exportData('income', params);
      const filename = `income-${todayLocalIso()}.csv`;
      downloadBlob(res.data, filename);
      toast.success('Downloaded!', { id: toastId });
    } catch (err: unknown) {
      console.error('Export failed:', err);
      toast.error('Export failed', { id: toastId });
    } finally {
      setExporting(false);
    }
  };

  // --- CRUD Handlers ---

  const handleOpenAdd = () => {
    setFormData(getDefaultFormData());
    setFormErrors({});
    setApplyToSimilar(false);
    setFormOpen(true);
  };
  handleOpenAddRef.current = handleOpenAdd;

  const handleOpenEdit = (income: IncomeEntry) => {
    if (income.type === 'POS') {
      toast('POS income entries are read-only', { icon: '\u2139\uFE0F' });
      return;
    }
    if (income.type === 'SETTLEMENT') {
      toast('Settlement income is read-only \u2014 edit the aggregator settlement instead', { icon: '\u2139\uFE0F' });
      return;
    }
    setFormData({
      id: income.id,
      amount: income.amount,
      vatAmount: income.vatAmount ?? '',
      source: income.source || '',
      category: income.category || '',
      // Hydrate the new range / recorded fields. Old rows only have `date`,
      // so fall back to that for `fromDate`.
      fromDate: income.fromDate || income.date,
      toDate: income.toDate || '',
      recordedDate: income.recordedDate || '',
      date: income.date,
      paymentMode: income.paymentMode || 'CASH',
      description: income.description || '',
    });
    setFormErrors({});
    setApplyToSimilar(false);
    setFormOpen(true);
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};
    if (!formData.amount || Number(formData.amount) <= 0) errors.amount = 'Enter a valid amount';
    if (!formData.source.trim()) errors.source = 'Source is required';
    if (!formData.category) errors.category = 'Category is required';
    if (!formData.fromDate) errors.fromDate = 'From date is required';
    if (formData.toDate && formData.fromDate && formData.toDate < formData.fromDate) {
      errors.toDate = 'To date must be on or after From date';
    }
    if (aggregatorSourceMatch) {
      // Inline banner already explains; this just blocks Save.
      errors.source = `${formData.source.trim()} is an aggregator — record income via Aggregator Settlements`;
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleFormChange = (field: keyof IncomeFormData, value: unknown) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (formErrors[field]) {
      setFormErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    setSaving(true);
    try {
      const payload: Record<string, unknown> = {
        amount: Number(formData.amount),
        vatAmount: formData.vatAmount === '' || formData.vatAmount === null || formData.vatAmount === undefined
          ? null
          : Number(formData.vatAmount),
        source: formData.source.trim(),
        category: formData.category,
        // Send the new range fields. We mirror `date` to `fromDate` so any
        // legacy code path still gets a value.
        fromDate: formData.fromDate,
        toDate: formData.toDate || null,
        recordedDate: formData.recordedDate || null,
        date: formData.fromDate,
        paymentMode: formData.paymentMode,
        description: formData.description.trim() || null,
      };

      if (formData.id) {
        await financeApi.updateIncome(formData.id, payload);

        // Bulk update similar entries if checkbox is checked
        if (applyToSimilar && formData.source.trim()) {
          try {
            // Extract the key part of the source for matching (first meaningful word/phrase)
            const sourceKey = formData.source.trim().split(' ref:')[0].split(' -')[0].trim();
            const bulkRes = await financeApi.bulkUpdateIncomeCategory(sourceKey, formData.category);
            const updated = bulkRes.data?.updatedCount || 0;
            toast.success(`Category updated for ${updated} similar entries`);
          } catch (bulkErr) {
            console.error('Bulk update failed:', bulkErr);
            toast('Could not update similar entries', { icon: '\u26A0\uFE0F' });
          }
        } else {
          toast.success('Income updated');
        }
      } else {
        await financeApi.createIncome(payload);
        toast.success('Income added');
      }
      setFormOpen(false);
      await Promise.all([fetchIncome(), fetchSummary()]);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to save income';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (income: IncomeEntry) => {
    if (income.type === 'POS') {
      toast('POS income entries cannot be deleted', { icon: '\u2139\uFE0F' });
      return;
    }
    if (income.type === 'SETTLEMENT') {
      toast('Settlement income cannot be deleted directly \u2014 delete the aggregator settlement instead', { icon: '\u2139\uFE0F' });
      return;
    }

    const confirmed = await confirm({
      title: 'Delete Income',
      description: `Delete this ${formatCurrency(income.amount)} income entry from "${income.source}"?`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
    });

    if (!confirmed) return;

    try {
      await financeApi.deleteIncome(income.id);
      toast.success('Income deleted');
      await Promise.all([fetchIncome(), fetchSummary()]);
    } catch (err: unknown) {
      console.error('Error deleting income:', err);
      toast.error('Failed to delete income');
    }
  };

  // --- Filter handlers ---

  const handleFilterChange = (field: keyof Filters, value: unknown) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
    setPage(0);
  };

  const handleClearFilters = () => {
    setFilters(DEFAULT_FILTERS);
    setPage(0);
  };

  // --- Render helpers ---

  const getCategoryChip = (category: string) => {
    const found = allIncomeCategories.find((c) => c.value === category);
    const label = found ? found.label : category?.replace(/_/g, ' ') || 'Unknown';
    return (
      <Chip
        label={label}
        size="small"
        sx={{
          fontWeight: 600,
          fontSize: '0.7rem',
          backgroundColor: theme.button.primary.bg,
          color: theme.button.primary.text,
          height: 22,
        }}
      />
    );
  };

  const getModeLabel = (mode: string) => PAYMENT_MODE_LABELS[mode] || mode;

  const formatDate = (dateStr: string) => {
    try {
      return new Date(dateStr).toLocaleDateString(CURRENCY.locale, {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      });
    } catch {
      return dateStr;
    }
  };

  // --- Shared Styles ---

  const selectSx = {
    borderRadius: '10px',
    backgroundColor: theme.components?.input?.background || '#FFFFFF',
    '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.components?.input?.border || '#DDD8D2' },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: theme.primary },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: theme.components?.input?.focus || theme.primary },
    '& .MuiSelect-select': { color: theme.text },
  };

  const cardSx = {
    borderRadius: '12px',
    backgroundColor: theme.components?.card?.background || '#FFFFFF',
    border: `1px solid ${theme.components?.card?.border || '#E0E0E0'}`,
  };

  // --- Filter configs for SearchFilterBar ---

  // --- Client-side fuzzy search ---
  // When filters.search is set, fetchIncome pulls a larger page without backend
  // `search`. We then fuzzy-match here so typos (e.g. "jest" → "Just Eat") work.
  const fuzzyKeys = useMemo(
    () => ['source', 'category', 'paymentMode', 'description', 'type'],
    [],
  );
  const fuzzyMatches = useFuzzy<IncomeEntry>(incomes, fuzzyKeys, filters.search, { threshold: 0.4 });
  const searchActive = !!filters.search.trim();
  const displayedIncomes = searchActive ? fuzzyMatches : incomes;
  const displayedTotal = searchActive ? displayedIncomes.length : totalElements;
  const displayedSummary = useMemo<IncomeSummary>(() => {
    if (!searchActive) return summary;
    const total = displayedIncomes.reduce((s, r) => s + (r.amount || 0), 0);
    const manual = displayedIncomes
      .filter((r) => r.type === 'MANUAL')
      .reduce((s, r) => s + (r.amount || 0), 0);
    const pos = displayedIncomes
      .filter((r) => r.type === 'POS')
      .reduce((s, r) => s + (r.amount || 0), 0);
    return { totalIncome: total, manualIncome: manual, posIncome: pos };
  }, [searchActive, displayedIncomes, summary]);

  const filterConfigs: FilterConfig[] = [
    {
      key: 'category',
      label: 'Category',
      options: [{ value: '', label: 'All' }, ...allIncomeCategories.map((c) => ({ value: c.value, label: c.label }))],
      value: filters.category,
      onChange: (value: string) => handleFilterChange('category', value),
      minWidth: 150,
    },
    {
      key: 'paymentMode',
      label: 'Mode',
      options: [{ value: '', label: 'All' }, ...PAYMENT_MODES],
      value: filters.paymentMode,
      onChange: (value: string) => handleFilterChange('paymentMode', value),
      minWidth: 130,
    },
    {
      key: 'type',
      label: 'Type',
      options: [
        { value: '', label: 'All' },
        { value: 'MANUAL', label: 'Manual' },
        { value: 'POS', label: 'POS' },
        { value: 'ADJUSTMENT', label: 'Adjustment' },
        { value: 'SETTLEMENT', label: 'Settlement' },
      ],
      value: filters.type,
      onChange: (value: string) => handleFilterChange('type', value),
      minWidth: 120,
    },
  ];

  // --- Table columns for DataTable ---

  const tableColumns: DataTableColumn<IncomeEntry>[] = [
    {
      id: 'date',
      label: 'Date',
      render: (row) => (
        <Typography sx={{ color: theme.gray?.[500] || '#666', fontSize: '0.813rem', whiteSpace: 'nowrap' }}>
          {formatDate(row.date)}
        </Typography>
      ),
    },
    {
      id: 'source',
      label: 'Source',
      render: (row) => (
        <Typography sx={{ color: theme.text, fontSize: '0.875rem', fontWeight: 500 }}>
          {row.source}
        </Typography>
      ),
    },
    {
      id: 'category',
      label: 'Category',
      render: (row) => getCategoryChip(row.category),
    },
    {
      id: 'amount',
      label: 'Amount',
      align: 'right' as const,
      render: (row) => (
        <Typography sx={{ fontWeight: 700, color: theme.success || '#4CAF50', fontSize: '0.95rem', whiteSpace: 'nowrap' }}>
          {formatCurrency(row.amount, { showDecimals: false })}
        </Typography>
      ),
    },
    {
      id: 'vatAmount',
      label: 'VAT',
      align: 'right' as const,
      minWidth: 80,
      render: (row) =>
        row.vatAmount && Number(row.vatAmount) > 0 ? (
          <Typography sx={{ fontWeight: 600, color: theme.gray?.[500] || '#666', fontSize: '0.875rem', whiteSpace: 'nowrap' }}>
            {formatCurrency(row.vatAmount, { showDecimals: false })}
          </Typography>
        ) : (
          <Typography component="span" sx={{ color: theme.gray?.[300] || '#CCC', fontSize: '0.875rem' }}>
            —
          </Typography>
        ),
    },
    {
      id: 'paymentMode',
      label: 'Mode',
      render: (row) => (
        <Chip
          label={getModeLabel(row.paymentMode)}
          size="small"
          sx={{
            fontWeight: 500,
            fontSize: '0.7rem',
            backgroundColor: theme.gray?.[100] || '#F0F0F0',
            color: theme.gray?.[500] || '#666',
            height: 24,
          }}
        />
      ),
    },
    {
      id: 'type',
      label: 'Type',
      render: (row) => {
        const typeInfo = TYPE_VARIANT_MAP[row.type] || { label: row.type, variant: 'info' as const };
        return <StatusChip status={typeInfo.label} variant={typeInfo.variant} size="small" sx={{ height: 24, fontSize: '0.7rem' }} />;
      },
    },
    {
      id: 'actions',
      label: '',
      align: 'right' as const,
      render: (row) => {
        // Finance-only-restricted stewards cannot edit or delete entries —
        // surface a read-only label in place of the action menu trigger.
        if (isFinanceOnlyRestricted) {
          return (
            <Typography variant="caption" sx={{ color: theme.gray?.[300] || '#CCC', fontStyle: 'italic' }}>
              Read-only
            </Typography>
          );
        }
        // Auto-derived rows (POS, SETTLEMENT) are read-only — edits flow
        // through the source record (POS order or aggregator settlement).
        const isAutoDerived = row.type === 'POS' || row.type === 'SETTLEMENT';
        return !isAutoDerived ? (
          <AppIconButton
            icon={<EllipsisVerticalIcon style={{ width: 16, height: 16 }} />}
            tooltip="Actions"
            variant="ghost"
            size="small"
            onClick={(e) => handleMenuOpen(e, row.id)}
          />
        ) : (
          <Typography variant="caption" sx={{ color: theme.gray?.[300] || '#CCC', fontStyle: 'italic' }}>
            Read-only
          </Typography>
        );
      },
    },
  ];

  // --- Skeletons ---

  const renderSkeletonCards = () =>
    Array.from({ length: 4 }).map((_, i) => (
      <Paper
        key={i}
        sx={{ ...cardSx, p: 2, mb: 1.5 }}
        elevation={0}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
          <Skeleton variant="rounded" width={100} height={24} sx={{ bgcolor: theme.components?.skeleton?.base || '#E0E0E0' }} />
          <Skeleton variant="rounded" width={80} height={28} sx={{ bgcolor: theme.components?.skeleton?.base || '#E0E0E0' }} />
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Skeleton variant="text" width={120} sx={{ bgcolor: theme.components?.skeleton?.base || '#E0E0E0' }} />
            <Skeleton variant="text" width={80} sx={{ bgcolor: theme.components?.skeleton?.base || '#E0E0E0' }} />
          </Box>
          <Skeleton variant="rounded" width={56} height={24} sx={{ bgcolor: theme.components?.skeleton?.base || '#E0E0E0' }} />
        </Box>
      </Paper>
    ));

  // --- Render ---

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 }, backgroundColor: theme.background, minHeight: '100vh', pb: { xs: 'calc(96px + env(safe-area-inset-bottom, 0px))', sm: 3 } }}>
      <FinanceTabBar />

      {/* Header — confident-shell hero band */}
      <PageHeroBand
        eyebrow="Finance · Income"
        title="Income"
        heroLabel="Filtered total"
        heroValue={formatCurrency(displayedSummary.totalIncome, { showDecimals: false })}
        actions={
          <Box sx={{ display: { xs: 'none', sm: 'flex' } }}>
            {/* Primary CTA keeps its saffron fill — good contrast on the dark band. */}
            <PrimaryButton
              startIcon={<PlusIcon style={{ width: 20, height: 20 }} />}
              onClick={handleOpenAdd}
              data-testid="add-income-btn"
              sx={{
                minHeight: 44,
                boxShadow: '0 2px 8px color-mix(in srgb, var(--action) 30%, transparent)',
                '&:hover': {
                  boxShadow: '0 4px 12px color-mix(in srgb, var(--action) 40%, transparent)',
                },
              }}
            >
              Add Income
            </PrimaryButton>
          </Box>
        }
      />

      {/* Summary Row */}
      <Box
        sx={{
          display: 'flex',
          gap: { xs: 1, sm: 2 },
          mb: { xs: 2, sm: 3 },
          overflowX: 'auto',
          pb: 0.5,
          '&::-webkit-scrollbar': { height: 4 },
          '&::-webkit-scrollbar-thumb': { backgroundColor: theme.gray?.[300] || '#CCC', borderRadius: 2 },
        }}
      >
        <Paper sx={{ ...cardSx, flex: '1 0 auto', minWidth: 120, p: { xs: 1.5, sm: 2 } }} elevation={0}>
          <Typography variant="caption" sx={{ color: theme.gray?.[500] || '#666', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.65rem', letterSpacing: '0.05em' }}>
            Total Income
          </Typography>
          <Typography
            sx={{
              fontWeight: 700,
              color: theme.success || '#4CAF50',
              fontSize: { xs: '1.1rem', sm: '1.35rem' },
              lineHeight: 1.2,
              mt: 0.25,
            }}
          >
            {formatCurrency(displayedSummary.totalIncome, { showDecimals: false })}
          </Typography>
        </Paper>
        <Paper sx={{ ...cardSx, flex: '1 0 auto', minWidth: 120, p: { xs: 1.5, sm: 2 } }} elevation={0}>
          <Typography variant="caption" sx={{ color: theme.gray?.[500] || '#666', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.65rem', letterSpacing: '0.05em' }}>
            Manual
          </Typography>
          <Typography
            sx={{
              fontWeight: 700,
              color: 'var(--text-link)',
              fontSize: { xs: '1.1rem', sm: '1.35rem' },
              lineHeight: 1.2,
              mt: 0.25,
            }}
          >
            {formatCurrency(displayedSummary.manualIncome, { showDecimals: false })}
          </Typography>
        </Paper>
        <Paper sx={{ ...cardSx, flex: '1 0 auto', minWidth: 120, p: { xs: 1.5, sm: 2 } }} elevation={0}>
          <Typography variant="caption" sx={{ color: theme.gray?.[500] || '#666', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.65rem', letterSpacing: '0.05em' }}>
            POS
          </Typography>
          <Typography
            sx={{
              fontWeight: 700,
              color: theme.success || '#4CAF50',
              fontSize: { xs: '1.1rem', sm: '1.35rem' },
              lineHeight: 1.2,
              mt: 0.25,
            }}
          >
            {formatCurrency(displayedSummary.posIncome, { showDecimals: false })}
          </Typography>
        </Paper>
        {summary.byMode && Object.entries(summary.byMode).map(([mode, amount]) => (
          <Paper key={mode} sx={{ ...cardSx, flex: '1 0 auto', minWidth: 100, p: { xs: 1.5, sm: 2 } }} elevation={0}>
            <Typography variant="caption" sx={{ color: theme.gray?.[500] || '#666', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.65rem', letterSpacing: '0.05em' }}>
              {getModeLabel(mode)}
            </Typography>
            <Typography
              sx={{
                fontWeight: 700,
                color: theme.text,
                fontSize: { xs: '1rem', sm: '1.2rem' },
                lineHeight: 1.2,
                mt: 0.25,
              }}
            >
              {formatCurrency(amount, { showDecimals: false })}
            </Typography>
          </Paper>
        ))}
      </Box>

      {/* Filter Bar */}
      <Paper sx={{ ...cardSx, mb: { xs: 2, sm: 3 }, overflow: 'hidden' }} elevation={0}>
        {/* Filter chip row — always visible */}
        <Box sx={{ p: { xs: 1.5, sm: 2 }, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          <Chip
            icon={<FunnelIcon style={{ width: 14, height: 14 }} />}
            label={
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                Filters
                {activeFilterCount > 0 && (
                  <span style={{
                    backgroundColor: theme.primary,
                    color: 'var(--text-inverse)',
                    borderRadius: 10,
                    padding: '0 6px',
                    fontSize: '0.65rem',
                    fontWeight: 700,
                    minWidth: 16,
                    textAlign: 'center',
                  }}>{activeFilterCount}</span>
                )}
              </span>
            }
            onClick={toggleFilters}
            data-testid="filter-toggle-btn"
            sx={{
              fontWeight: 600,
              fontSize: '0.75rem',
              minHeight: 32,
              backgroundColor: showFilters || activeFilterCount > 0 ? `${theme.primary}20` : theme.gray?.[100] || '#F0F0F0',
              color: showFilters || activeFilterCount > 0 ? theme.primary : theme.gray?.[500] || '#666',
              border: showFilters || activeFilterCount > 0 ? `1.5px solid ${theme.primary}` : `1px solid ${theme.gray?.[200] || '#E0E0E0'}`,
              cursor: 'pointer',
              '&:hover': { opacity: 0.85 },
              '& .MuiChip-icon': { color: 'inherit' },
            }}
          />
          {activeFilterCount > 0 && (
            <Chip
              icon={<XMarkIcon style={{ width: 14, height: 14 }} />}
              label="Clear"
              onClick={handleClearFilters}
              sx={{
                fontWeight: 600,
                fontSize: '0.75rem',
                minHeight: 32,
                backgroundColor: `${theme.danger || '#D99982'}15`,
                color: theme.danger || '#D99982',
                border: `1px solid ${theme.danger || '#D99982'}50`,
                cursor: 'pointer',
                '& .MuiChip-icon': { color: 'inherit' },
              }}
            />
          )}
        </Box>

        {/* Collapsed by default; toggle via Filters chip on all viewports. */}
        <Collapse in={showFilters} timeout="auto">
          {/* Date range — preset chips + shared responsive from/to pair */}
          <Box sx={{ px: { xs: 1.5, sm: 2 }, pb: 1, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {DATE_PRESETS.map((preset) => (
                <Chip
                  key={preset.label}
                  label={preset.label}
                  onClick={() => {
                    const { start, end } = preset.getValue();
                    setFilters((prev) => ({ ...prev, dateFrom: start, dateTo: end }));
                    setPage(0);
                  }}
                  sx={{
                    backgroundColor: `${theme.primary}15`,
                    color: theme.primary,
                    fontWeight: 500,
                    cursor: 'pointer',
                    '&:hover': { backgroundColor: `${theme.primary}25` },
                  }}
                />
              ))}
            </Box>
            <FinanceDateRange
              from={filters.dateFrom}
              to={filters.dateTo}
              onFromChange={(v) => {
                setFilters((prev) => ({ ...prev, dateFrom: v }));
                setPage(0);
              }}
              onToChange={(v) => {
                setFilters((prev) => ({ ...prev, dateTo: v }));
                setPage(0);
              }}
              showLabels
            />
          </Box>

          {/* Period chips */}
          <Box sx={{ px: { xs: 1.5, sm: 2 }, pb: 1, display: 'flex', gap: 1, alignItems: 'center' }}>
            {(['DAILY', 'WEEKLY', 'MONTHLY'] as PeriodView[]).map((p) => {
              const isActive = periodView === p;
              return (
                <Chip
                  key={p}
                  label={p.charAt(0) + p.slice(1).toLowerCase()}
                  onClick={() => setPeriodView(p)}
                  data-testid={`period-chip-${p.toLowerCase()}`}
                  sx={{
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    minHeight: 32,
                    backgroundColor: isActive ? `${theme.primary}20` : theme.gray?.[100] || '#F0F0F0',
                    color: isActive ? theme.primary : theme.gray?.[500] || '#666',
                    border: isActive ? `1.5px solid ${theme.primary}` : `1px solid ${theme.gray?.[200] || '#E0E0E0'}`,
                    cursor: 'pointer',
                    '&:hover': { opacity: 0.85 },
                  }}
                />
              );
            })}
          </Box>

          {/* Search + dropdown filters */}
          <Box sx={{ px: { xs: 1.5, sm: 2 }, pb: 1 }}>
            <SearchFilterBar
              searchPlaceholder="Search source..."
              searchValue={filters.search}
              onSearchChange={(value) => handleFilterChange('search', value)}
              filters={filterConfigs}
            />
          </Box>

          {/* Action buttons */}
          <Box sx={{ p: { xs: 1.5, sm: 2 }, display: 'flex', gap: 1, justifyContent: { xs: 'stretch', sm: 'flex-end' }, flexWrap: 'wrap' }}>
            <SecondaryButton
              onClick={handleExportCsv}
              disabled={exporting}
              startIcon={<ArrowDownTrayIcon style={{ width: 16, height: 16 }} />}
              data-testid="export-csv-btn"
              sx={{ fontSize: '0.75rem', flex: { xs: 1, sm: 'none' } }}
            >
              Export
            </SecondaryButton>
            <SecondaryButton
              onClick={handleRefresh}
              disabled={refreshing}
              startIcon={<ArrowPathIcon style={{ width: 16, height: 16 }} className={refreshing ? 'animate-spin' : ''} />}
              data-testid="refresh-btn"
              sx={{ fontSize: '0.75rem', flex: { xs: 1, sm: 'none' } }}
            >
              Refresh
            </SecondaryButton>
          </Box>
        </Collapse>
      </Paper>

      {/* Filter-aware totals — total over the filtered set + entry count */}
      <FilteredTotals
        data-testid="income-filtered-totals"
        items={[
          { label: 'Total', value: formatCurrency(displayedSummary.totalIncome, { showDecimals: false }) },
          { label: 'Entries', value: String(displayedTotal) },
        ]}
      />

      {/* Income List */}
      {isMobile ? (
        <Paper sx={{ ...cardSx, overflow: 'hidden' }} elevation={0}>
          <Box sx={{ p: 1.5 }}>
            {loading ? (
              renderSkeletonCards()
            ) : displayedIncomes.length === 0 ? (
              <EmptyState
                icon={<BanknotesIcon style={{ width: 44, height: 44, color: 'var(--text-tertiary)' }} />}
                title="No income recorded yet"
                subtitle="Tap + to add your first entry"
                actionButton={
                  <PrimaryButton
                    startIcon={<PlusIcon style={{ width: 18, height: 18 }} />}
                    onClick={handleOpenAdd}
                    data-testid="add-income-empty-btn"
                    sx={{ minHeight: 44 }}
                  >
                    Add Income
                  </PrimaryButton>
                }
              />
            ) : (
              displayedIncomes.map((income) => (
                <Paper
                  key={income.id}
                  onClick={isFinanceOnlyRestricted ? undefined : () => handleOpenEdit(income)}
                  sx={{
                    p: 2,
                    mb: 1.5,
                    borderRadius: '10px',
                    backgroundColor: theme.components?.card?.background || '#FFFFFF',
                    border: `1px solid ${theme.components?.card?.border || '#E0E0E0'}`,
                    // Auto-derived rows (POS, SETTLEMENT) are non-interactive —
                    // edits happen via the source record.
                    cursor: (income.type === 'POS' || income.type === 'SETTLEMENT') ? 'default' : 'pointer',
                    transition: 'all 0.15s ease',
                    '&:active': (income.type !== 'POS' && income.type !== 'SETTLEMENT') ? { transform: 'scale(0.98)' } : {},
                    '&:hover': (income.type !== 'POS' && income.type !== 'SETTLEMENT') ? { borderColor: theme.primary + '66' } : {},
                  }}
                  elevation={0}
                >
                  {/* Top row: source + amount */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        variant="body2"
                        noWrap
                        sx={{ fontWeight: 600, color: theme.text, fontSize: '0.875rem', mb: 0.5 }}
                      >
                        {income.source}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        {getCategoryChip(income.category)}
                        {(() => {
                          const typeInfo = TYPE_VARIANT_MAP[income.type] || { label: income.type, variant: 'info' as const };
                          return <StatusChip status={typeInfo.label} variant={typeInfo.variant} size="small" sx={{ height: 24, fontSize: '0.7rem' }} />;
                        })()}
                      </Box>
                    </Box>
                    <Typography
                      sx={{
                        fontWeight: 700,
                        color: theme.success || '#4CAF50',
                        fontSize: '1.1rem',
                        flexShrink: 0,
                        ml: 1,
                      }}
                    >
                      {formatCurrency(income.amount, { showDecimals: false })}
                    </Typography>
                  </Box>

                  {/* Bottom row: date + mode + delete */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      <Typography variant="caption" sx={{ color: theme.gray?.[500] || '#666' }}>
                        {formatDate(income.date)}
                      </Typography>
                      <Chip
                        label={getModeLabel(income.paymentMode)}
                        size="small"
                        sx={{
                          fontWeight: 500,
                          fontSize: '0.65rem',
                          backgroundColor: theme.gray?.[100] || '#F0F0F0',
                          color: theme.gray?.[500] || '#666',
                          height: 20,
                        }}
                      />
                    </Box>
                    {income.type !== 'POS' && income.type !== 'SETTLEMENT' && !isFinanceOnlyRestricted && (
                      <AppIconButton
                        icon={<EllipsisVerticalIcon style={{ width: 16, height: 16 }} />}
                        tooltip="Actions"
                        variant="ghost"
                        size="small"
                        onClick={(e) => handleMenuOpen(e, income.id)}
                        sx={{ width: 32, height: 32 }}
                      />
                    )}
                  </Box>
                </Paper>
              ))
            )}
          </Box>

          {/* Mobile Pagination */}
          {displayedTotal > 0 && !loading && (
            <Box
              sx={{
                borderTop: `1px solid ${theme.components?.card?.border || '#E0E0E0'}`,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                px: 1.5,
                py: 1,
              }}
            >
              <Typography variant="caption" sx={{ color: theme.gray?.[500] || '#666' }}>
                {`${page * rowsPerPage + 1}–${Math.min((page + 1) * rowsPerPage, displayedTotal)} of ${displayedTotal}`}
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <AppIconButton
                  icon={<span style={{ fontSize: 18, lineHeight: 1 }}>{'‹'}</span>}
                  tooltip="Previous"
                  variant="ghost"
                  size="small"
                  disabled={page === 0}
                  onClick={() => setPage(page - 1)}
                />
                <AppIconButton
                  icon={<span style={{ fontSize: 18, lineHeight: 1 }}>{'›'}</span>}
                  tooltip="Next"
                  variant="ghost"
                  size="small"
                  disabled={(page + 1) * rowsPerPage >= displayedTotal}
                  onClick={() => setPage(page + 1)}
                />
              </Box>
            </Box>
          )}
        </Paper>
      ) : (
        /* Desktop: DataTable */
        <DataTable<IncomeEntry>
          columns={tableColumns}
          data={displayedIncomes}
          loading={loading}
          emptyState={
            <EmptyState
              icon={<BanknotesIcon style={{ width: 40, height: 40, color: 'var(--text-tertiary)' }} />}
              title="No income recorded yet"
              subtitle='Click "Add Income" to record your first entry'
            />
          }
          pagination={{
            page,
            rowsPerPage,
            total: displayedTotal,
            onPageChange: setPage,
            onRowsPerPageChange: (rpp) => {
              setRowsPerPage(rpp);
              setPage(0);
            },
            rowsPerPageOptions: ROWS_PER_PAGE_OPTIONS,
          }}
          rowKey={(row) => String(row.id)}
          onRowClick={isFinanceOnlyRestricted ? undefined : handleOpenEdit}
          sx={denseTableSx}
        />
      )}

      {/* Mobile FAB */}
      {isMobile && (
        <Fab
          onClick={handleOpenAdd}
          aria-label="Add Income"
          data-testid="add-income-fab"
          sx={{
            position: 'fixed',
            bottom: 80,
            right: 20,
            zIndex: 1000,
            backgroundColor: theme.button?.primary?.bg || 'var(--action)',
            color: theme.button?.primary?.text || '#FFFFFF',
            width: 56,
            height: 56,
            boxShadow: '0 4px 16px color-mix(in srgb, var(--action) 40%, transparent)',
            '&:hover': {
              backgroundColor: theme.button?.primary?.hover || 'var(--action-hover)',
              boxShadow: '0 6px 20px color-mix(in srgb, var(--action) 50%, transparent)',
            },
          }}
        >
          <PlusIcon style={{ width: 28, height: 28 }} />
        </Fab>
      )}

      {/* Add/Edit Income Modal */}
      <Modal
        isOpen={formOpen}
        onClose={() => setFormOpen(false)}
        title={isEdit ? 'Edit Income' : 'Add Income'}
        subtitle="Required fields marked with *"
        maxWidth="md"
        mobileFullScreen
        footer={
          <FormActions
            onCancel={() => setFormOpen(false)}
            onSubmit={handleSave}
            submitLabel={isEdit ? 'Update Income' : 'Save Income'}
            loading={saving}
          />
        }
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleSave();
          }}
          noValidate
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            {/* Amount - large and prominent */}
            <FormInput
              label="Amount *"
              type="number"
              value={formData.amount}
              onChange={(e) => handleFormChange('amount', e.target.value ? Number(e.target.value) : '')}
              error={Boolean(formErrors.amount)}
              helperText={formErrors.amount}
              fullWidth
              autoFocus
              data-testid="income-amount-input"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Typography sx={{ fontWeight: 700, color: theme.primary, fontSize: '1.1rem' }}>
                      {CURRENCY.symbol}
                    </Typography>
                  </InputAdornment>
                ),
                sx: { fontSize: '1.25rem', fontWeight: 600 },
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  minHeight: 56,
                },
                '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': {
                  WebkitAppearance: 'none',
                  margin: 0,
                },
                '& input[type=number]': {
                  MozAppearance: 'textfield',
                },
              }}
              inputProps={{ min: 0, step: 0.01 }}
            />

            {/* VAT (UK-style single tax field) */}
            <FormInput
              label="VAT"
              type="number"
              value={formData.vatAmount}
              onChange={(e) => handleFormChange('vatAmount', e.target.value ? Number(e.target.value) : '')}
              fullWidth
              placeholder="0.00"
              helperText="Manual override — leave blank if not applicable"
              data-testid="income-vat-input"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Typography sx={{ fontWeight: 600, color: theme.primary, fontSize: '0.95rem' }}>
                      {CURRENCY.symbol}
                    </Typography>
                  </InputAdornment>
                ),
              }}
              inputProps={{ min: 0, step: 0.01 }}
            />

            {/* Source */}
            <FormInput
              label="Source *"
              value={formData.source}
              onChange={(e) => handleFormChange('source', e.target.value)}
              error={Boolean(formErrors.source) || Boolean(aggregatorSourceMatch)}
              helperText={!aggregatorSourceMatch ? formErrors.source : undefined}
              fullWidth
              placeholder="e.g. Birthday party catering, Rental income"
              data-testid="income-source-input"
            />

            {aggregatorSourceMatch && (
              <Box
                data-testid="aggregator-source-warning"
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: { xs: 'stretch', sm: 'center' },
                  justifyContent: 'space-between',
                  gap: 1.25,
                  p: 1.5,
                  borderRadius: '10px',
                  backgroundColor: `${theme.warning || '#E0A458'}15`,
                  border: `1px solid ${theme.warning || '#E0A458'}55`,
                }}
              >
                <Typography variant="body2" sx={{ color: theme.text, fontSize: '0.8125rem', lineHeight: 1.4 }}>
                  <strong>{formData.source.trim()}</strong> is an Online Aggregator channel. Record income via
                  the Aggregators page so it matches the actual bank payout.
                </Typography>
                <SecondaryButton
                  onClick={() => {
                    setFormOpen(false);
                    navigate('/vendor/finance/aggregators');
                  }}
                  sx={{ fontSize: '0.75rem', whiteSpace: 'nowrap' }}
                  data-testid="aggregator-source-nav-btn"
                >
                  Go to Aggregators &rarr;
                </SecondaryButton>
              </Box>
            )}

            {/* Category */}
            <FormControl fullWidth error={Boolean(formErrors.category)}>
              <InputLabel sx={{ color: theme.gray?.[500] || '#666', '&.Mui-focused': { color: theme.primary } }}>
                Category *
              </InputLabel>
              <Select
                value={formData.category}
                label="Category *"
                onChange={(e) => handleFormChange('category', e.target.value)}
                sx={selectSx}
                data-testid="income-category-select"
              >
                {allIncomeCategories.map((cat) => (
                  <MenuItem key={cat.value} value={cat.value}>{cat.label}</MenuItem>
                ))}
                {/* Restricted finance-only stewards cannot create categories. */}
                {!isFinanceOnlyRestricted && (
                  <MenuItem
                    value="__add__"
                    onClick={async (e) => {
                      e.stopPropagation();
                      const name = prompt('Enter new income category name:');
                      if (name && name.trim()) {
                        try {
                          const res = await financeApi.createIncomeCategory({ name: name.trim() });
                          await fetchIncomeCategories();
                          handleFormChange('category', res.data.name);
                        } catch (err: unknown) {
                          const message = err instanceof Error ? err.message : 'Failed to create category';
                          alert(message);
                        }
                      }
                    }}
                    sx={{ color: theme.primary || 'var(--action)', fontWeight: 600, borderTop: `1px solid ${theme.gray?.[200] || '#E0E0E0'}`, mt: 0.5 }}
                  >
                    + Add Category
                  </MenuItem>
                )}
              </Select>
              {formErrors.category && (
                <Typography variant="caption" sx={{ color: theme.danger, mt: 0.5, ml: 1.5 }}>
                  {formErrors.category}
                </Typography>
              )}
            </FormControl>

            {/* Dates: From (required), To (optional range), Recorded (defaults to today).
                Stacks on mobile, side-by-side from sm+ to keep the form compact. */}
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
              <FormInput
                label="From Date *"
                type="date"
                value={formData.fromDate}
                onChange={(e) => handleFormChange('fromDate', e.target.value)}
                error={Boolean(formErrors.fromDate)}
                helperText={formErrors.fromDate}
                fullWidth
                InputLabelProps={{ shrink: true }}
                data-testid="income-from-date-input"
              />
              <FormInput
                label="To Date"
                type="date"
                value={formData.toDate ?? ''}
                onChange={(e) => handleFormChange('toDate', e.target.value)}
                error={Boolean(formErrors.toDate)}
                helperText={formErrors.toDate || 'Leave blank for single day'}
                fullWidth
                InputLabelProps={{ shrink: true }}
                data-testid="income-to-date-input"
              />
              <FormInput
                label="Recorded Date"
                type="date"
                value={formData.recordedDate ?? ''}
                onChange={(e) => handleFormChange('recordedDate', e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
                helperText="Defaults to today"
                data-testid="income-recorded-date-input"
              />
            </Box>

            {/* Payment Mode (vendor-configurable; supports inline add) */}
            <PaymentModeSelect
              value={formData.paymentMode}
              onChange={(v) => handleFormChange('paymentMode', v)}
              label="Payment Mode"
              fullWidth
              data-testid="income-payment-mode-select"
            />

            {/* Description */}
            <FormInput
              label="Notes (optional)"
              value={formData.description}
              onChange={(e) => handleFormChange('description', e.target.value)}
              fullWidth
              multiline
              minRows={2}
              maxRows={4}
              placeholder="Optional description or reference"
              data-testid="income-description-input"
            />

            {/* Bulk update checkbox — only shown when editing */}
            {formData.id && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1,
                  p: 1.5,
                  borderRadius: '10px',
                  backgroundColor: applyToSimilar ? theme.button.primary.bg : `${theme.gray?.[50] || '#F8F8F6'}`,
                  border: `1px solid ${applyToSimilar ? theme.primary + '40' : theme.gray?.[200] || '#E8E5E0'}`,
                }}
              >
                <Checkbox
                  checked={applyToSimilar}
                  onChange={(e) => setApplyToSimilar(e.target.checked)}
                  data-testid="apply-to-similar-checkbox"
                  sx={{ p: 0.5, color: theme.primary, '&.Mui-checked': { color: theme.primary } }}
                />
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: theme.text, fontSize: '0.813rem' }}>
                    Apply category to all similar entries
                  </Typography>
                  <Typography variant="caption" sx={{ color: theme.gray?.[500] || '#888', fontSize: '0.7rem' }}>
                    Updates all income entries with a similar source name to &ldquo;{allIncomeCategories.find(c => c.value === formData.category)?.label || formData.category}&rdquo;
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>
        </form>
      </Modal>

      {/* Shared Actions Menu for income rows */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: {
              borderRadius: '10px',
              border: `1px solid ${theme.components?.card?.border || '#E0E0E0'}`,
              boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
              minWidth: 140,
            },
          },
        }}
      >
        <MenuItem
          onClick={() => {
            const income = incomes.find((i) => i.id === menuActiveId);
            if (income) handleOpenEdit(income);
            handleMenuClose();
          }}
          sx={{ fontSize: '0.875rem', py: 1 }}
        >
          <ListItemIcon><PencilSquareIcon style={{ width: 16, height: 16, color: theme.primary }} /></ListItemIcon>
          <ListItemText primaryTypographyProps={{ fontSize: '0.875rem' }}>Edit</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            const income = incomes.find((i) => i.id === menuActiveId);
            if (income) handleDelete(income);
            handleMenuClose();
          }}
          sx={{ fontSize: '0.875rem', py: 1, color: theme.danger || '#D99982' }}
        >
          <ListItemIcon><TrashIcon style={{ width: 16, height: 16, color: theme.danger || '#D99982' }} /></ListItemIcon>
          <ListItemText primaryTypographyProps={{ fontSize: '0.875rem', color: theme.danger || '#D99982' }}>Delete</ListItemText>
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default IncomeManagement;
